import React, { useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, Flame } from "lucide-react";
import CoinLogo from "@/components/CoinLogo";
import BondingCurveBar from "@/components/BondingCurveBar";
import SafetyBadges from "@/components/SafetyBadges";
import VerifiedCheck from "@/components/VerifiedCheck";
import { shortAddr } from "@/lib/api";
import { fmtSui, fmtUsd, timeAgo } from "@/lib/format";
import { isTouch } from "@/lib/perf";

export default function CoinCard({ coin, suiUsd = null, devLock = null }) {
  const navigate = useNavigate();
  const mcUsd = suiUsd ? coin.market_cap_sui * suiUsd : null;
  const vol24 = coin.vol_24h_sui || 0;
  const hot = (coin.buys_1h || 0) > 0;

  const cardRef = useRef(null);
  const prevSui = useRef(undefined);
  const animRef = useRef(null);

  // Real-time trade flash: a buy (increase) glows green, a sell (decrease) glows red. Skipped
  // on touch devices — continuous per-card animation is the main lag source on Slush mobile.
  useEffect(() => {
    const cur = coin.real_sui_collected;
    const prev = prevSui.current;
    prevSui.current = cur;
    if (isTouch) return;
    if (prev === undefined || cur === prev || typeof cur !== "number") return;
    const el = cardRef.current;
    if (!el || !el.animate) return;
    const buy = cur > prev;
    const glow = buy ? "rgba(34,197,94,0.5)" : "rgba(244,63,94,0.5)";
    if (animRef.current) animRef.current.cancel();
    animRef.current = el.animate(
      [
        { boxShadow: "0 0 0px rgba(0,0,0,0)", transform: "translateY(0)" },
        { boxShadow: `0 0 26px ${glow}`, transform: "translateY(-2px)", offset: 0.22 },
        { boxShadow: `0 0 14px ${glow}`, transform: "translateY(0)", offset: 0.6 },
        { boxShadow: "0 0 0px rgba(0,0,0,0)", transform: "translateY(0)" },
      ],
      { duration: 850, easing: "ease-out" }
    );
  }, [coin.real_sui_collected]);

  return (
    <div
      ref={cardRef}
      data-testid={`coin-card-${coin.id || coin.curve_id}`}
      onClick={() => navigate(`/coin/${coin.id || coin.curve_id}`)}
      className="card-coin cursor-pointer p-4"
    >
      <div className="flex items-center gap-3">
        <div className="relative shrink-0">
          <div className="rounded-xl p-[1.5px]" style={{ background: "linear-gradient(135deg,#1EC62E,#7CF24E)" }}>
            <CoinLogo coin={coin} size={52} />
          </div>
          {hot && (
            <span className="absolute -top-1.5 -right-1.5 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[#FF6B00] text-white text-[8px] font-bold shadow-lg">
              <Flame className="w-2.5 h-2.5" /> HOT
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="font-display font-bold text-[15px] truncate">{coin.name}</span>
            {coin.verified && <VerifiedCheck size={15} />}
            <span className="font-mono text-xs text-[#1EC62E] shrink-0">${coin.symbol}</span>
          </div>
          <div className="text-[11px] text-muted-foreground font-mono mt-0.5 truncate">
            by <span onClick={(e) => { e.stopPropagation(); navigate(`/u/${coin.creator}`); }} className="hover:text-[#1EC62E] cursor-pointer" data-testid={`coin-creator-${coin.id || coin.curve_id}`}>{shortAddr(coin.creator)}</span> · {timeAgo(coin.created_at)}
          </div>
        </div>
        {coin.migrated && (
          <span className="shrink-0 self-start text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded-md text-black" style={{ background: "linear-gradient(90deg,#00E5FF,#1EC62E)" }}>
            Cetus
          </span>
        )}
      </div>

      <p className="text-xs text-muted-foreground mt-3 line-clamp-2 h-8">{coin.description}</p>

      <SafetyBadges coin={coin} devLock={devLock} size="sm" className="mt-2.5" />

      <div className="flex items-end justify-between mt-3 mb-3.5">
        <div>
          <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Market cap</div>
          <div className="font-display font-extrabold text-lg leading-none mt-1">{mcUsd == null ? "—" : fmtUsd(mcUsd)}</div>
        </div>
        <div className="flex items-center gap-3.5">
          <div className="text-right">
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">24h vol</div>
            <div className="font-mono text-xs text-[#1EC62E] mt-1">{fmtSui(vol24, 2)}</div>
          </div>
          <div className="flex items-center gap-1 text-muted-foreground text-xs">
            <MessageCircle className="w-3.5 h-3.5" /> {coin.reply_count || 0}
          </div>
        </div>
      </div>

      <BondingCurveBar
        progress={coin.progress}
        migrated={coin.migrated}
        current={coin.real_sui_collected}
        remaining={coin.sui_remaining}
        gradSui={coin.graduation_sui || 7500}
        showLabel
      />
    </div>
  );
}
